import React, { useEffect, useState, useMemo } from "react";
import { MaterialReactTable } from "material-react-table";
import { useNavigate } from "react-router-dom";
import FunctionsIcon from "@mui/icons-material/Functions";
import ConstructionIcon from "@mui/icons-material/Construction";
import {
  Button,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Autocomplete,
  Chip,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import { secondAxios } from "../../../../api/axios";
import paths from "../../../../api/paths.api";
import DocumentScannerIcon from "@mui/icons-material/DocumentScanner";
import DescriptionIcon from "@mui/icons-material/Description";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AssignmentIcon from "@mui/icons-material/Assignment";
import ChangeCircleIcon from "@mui/icons-material/ChangeCircle";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ChangeHistoryIcon from "@mui/icons-material/ChangeHistory";

import { mrtSurfaceProps } from "../../../../styles/mrtThemeProps";
import PlantModuleNav from "../../../../components/PlantModuleNav";
import { useAuthContext } from "../../../../context/authContext";
import useModuleAccess from "../../../../hooks/useModuleAccess";
import { filterColumnsByLicence } from "../../../../constants/appModules";
import ClientsCrudModal from "./ClientsCrudModal";

const buildPlantName = (prefix, number) =>
  [prefix, number].filter(Boolean).join("-");

/** Fixed width shared by all icon-only navigation columns */
const PLANT_ICON_COLUMN_SIZE = 120;

/** Theme-aware column separators between icon columns */
const getPlantColumnSeparatorSx = (theme) => ({
  borderLeft: `2px solid ${theme.palette.divider}`,
  borderRight: `1px solid ${theme.palette.divider}`,
});

const getPlantCellBorderSx = (theme) => ({
  borderRight: `1px solid ${theme.palette.divider}`,
});

/** Shared typography for every table header label */
const plantTableHeadCellFontSx = {
  fontSize: "1rem",
  fontWeight: "bold",
  fontStyle: "normal",
  fontFamily: "inherit",
};

const plantTableHeaderLabelSx = {
  display: "block",
  textAlign: "center",
  lineHeight: 1.15,
  ...plantTableHeadCellFontSx,
};

/** MRT shallow-merges head cell props — column `sx` replaces table `sx`; keep these rules on every narrow/icon column */
const mrtHeadCellKeepLabelsVisibleSx = {
  ...plantTableHeadCellFontSx,
  "& .Mui-TableHeadCell-Content": {
    width: "100%",
    minHeight: "2.25rem",
    alignItems: "center",
    justifyContent: "center",
    flexWrap: "wrap",
  },
  "& .Mui-TableHeadCell-Content-Labels": {
    overflow: "visible",
    width: "100%",
    justifyContent: "center",
  },
  "& .Mui-TableHeadCell-Content-Wrapper": {
    overflow: "visible !important",
    textOverflow: "clip",
    whiteSpace: "normal !important",
    minWidth: "0 !important",
    width: "100%",
    maxWidth: "100%",
    textAlign: "center",
    ...plantTableHeadCellFontSx,
  },
  "& .Mui-TableHeadCell-Content-Actions": {
    display: "none",
  },
};

const createPlantIconColumnDefaults = (separatorSx) => ({
  size: PLANT_ICON_COLUMN_SIZE,
  minSize: PLANT_ICON_COLUMN_SIZE,
  maxSize: PLANT_ICON_COLUMN_SIZE,
  grow: false,
  enableSorting: false,
  enableColumnActions: false,
  muiTableHeadCellProps: {
    sx: {
      ...mrtHeadCellKeepLabelsVisibleSx,
      whiteSpace: "normal",
      textAlign: "center",
      verticalAlign: "middle",
      lineHeight: 1.2,
      py: 0.75,
      backgroundColor: "background.default",
      ...separatorSx,
    },
  },
  muiTableBodyCellProps: {
    sx: {
      textAlign: "center",
      verticalAlign: "middle",
      px: 0.5,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: "background.paper",
      ...separatorSx,
      "& .Mui-TableBodyCell-Content-Wrapper": {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
      },
    },
  },
});

export default function PlantsTable({ isEstimation }) {
  const theme = useTheme();
  const plantIconColumnSeparatorSx = useMemo(
    () => getPlantColumnSeparatorSx(theme),
    [theme],
  );
  const plantCellBorderSx = useMemo(() => getPlantCellBorderSx(theme), [theme]);
  const plantIconColumnDefaults = useMemo(
    () => createPlantIconColumnDefaults(plantIconColumnSeparatorSx),
    [plantIconColumnSeparatorSx],
  );
  const navigate = useNavigate();
  const [plants, setPlants] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [state, setState] = useState({
    isLoading: false,
    isError: false,
  });
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("create"); // 'create' or 'edit'
  const [clientsModalOpen, setClientsModalOpen] = useState(false);
  const [allClients, setAllClients] = useState([]);
  const [currentPlant, setCurrentPlant] = useState({
    name: "",
    namePrefix: "",
    nameNumber: "",
    clientId: "",
    description: "",
    sharepointLink: "",
    people: [],
  });
  const { user } = useAuthContext();
  const { hasModule, allowedModules } = useModuleAccess();
  console.log("user", user.client);

  const [aimsSendingId, setAimsSendingId] = useState(null);

  const handleSendToAIMS = async (plantId) => {
    if (!plantId || aimsSendingId) return;
    try {
      setAimsSendingId(plantId);
      await secondAxios.put(`/plant/${plantId}/aims-sent`);
      setPlants((prev) =>
        prev.map((p) => (p.id === plantId ? { ...p, aimsSent: true } : p)),
      );
    } catch (err) {
      console.error("Error sending plant to AIMS:", err);
      setState((prev) => ({ ...prev, isError: true }));
    } finally {
      setAimsSendingId(null);
    }
  };

  // Load users for people selection
  const fetchUsers = async () => {
    try {
      const res = await secondAxios.get("/user");
      setAllUsers(res.data || []);
    } catch (err) {
      console.error("Error fetching users:", err);
    }
  };

  const fetchClients = async () => {
    try {
      const res = await secondAxios.get("/client");
      setAllClients(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Error fetching clients:", err);
    }
  };

  // Load plants
  const fetchPlants = async () => {
    try {
      setState({ isLoading: true, isError: false });
      const res = await secondAxios.get("/plant");
      console.log("Fetched plants:", res.data);

      const adminClient = process.env.REACT_APP_CLIENT_NAME;
      const filteredPlants =
        user.client === adminClient
          ? res.data
          : Array.isArray(res.data)
            ? res.data.filter((plant) => {
                const clientName = plant.client?.name;
                const clientPrefix = plant.client?.prefix;
                return (
                  clientName === user.client || clientPrefix === user.client
                );
              })
            : [];

      setPlants(filteredPlants);
    } catch (err) {
      console.error("Error fetching plants:", err);
      setState({ isLoading: false, isError: true });
    } finally {
      setState((prev) => ({ ...prev, isLoading: false }));
    }
  };

  useEffect(() => {
    if (user.client) {
      console.log("fetching plants");
      fetchPlants();
    }
    fetchUsers();
    fetchClients();
  }, [user]);

  // Open modal for creating new plant
  const handleOpenCreateModal = () => {
    setModalMode("create");
    setCurrentPlant({
      name: "",
      namePrefix: "",
      nameNumber: "",
      clientId: "",
      description: "",
      sharepointLink: "",
      people: [],
    });
    setModalOpen(true);
  };

  // Open modal for editing existing plant
  const handleOpenEditModal = (plant) => {
    setModalMode("edit");
    // Extract user IDs from people array if it exists
    const peopleIds = plant.people
      ? plant.people.map((user) => (typeof user === "string" ? user : user.id))
      : [];
    setCurrentPlant({
      ...plant,
      namePrefix: "",
      nameNumber: "",
      clientId: plant.clientId || plant.client?.id || "",
      people: peopleIds,
    });
    setModalOpen(true);
  };

  // Close modal
  const handleCloseModal = () => {
    setModalOpen(false);
    setCurrentPlant({
      name: "",
      namePrefix: "",
      nameNumber: "",
      clientId: "",
      description: "",
      sharepointLink: "",
      people: [],
    });
  };

  // Handle form input changes
  const handleInputChange = (field, value) => {
    setCurrentPlant((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleClientChange = (client) => {
    const prefix = client?.prefix
      ? String(client.prefix).replace(/[^a-zA-Z0-9]/g, "").toUpperCase()
      : "";
    setCurrentPlant((prev) => {
      const next = {
        ...prev,
        clientId: client?.id || "",
        namePrefix: prefix,
      };
      return {
        ...next,
        name:
          modalMode === "create"
            ? buildPlantName(next.namePrefix, next.nameNumber)
            : prev.name,
      };
    });
  };

  const handleNameNumberChange = (value) => {
    // Allow strings like p001 or p-001
    const sanitized = value.replace(/[^a-zA-Z0-9-]/g, "");
    setCurrentPlant((prev) => {
      const next = { ...prev, nameNumber: sanitized };
      return {
        ...next,
        name: buildPlantName(next.namePrefix, next.nameNumber),
      };
    });
  };

  // Handle people selection change
  const handlePeopleChange = (selectedUsers) => {
    const userIds = selectedUsers.map((user) =>
      typeof user === "string" ? user : user.id,
    );
    setCurrentPlant((prev) => ({
      ...prev,
      people: userIds,
    }));
  };

  // Save plant (create or update)
  const handleSavePlant = async () => {
    try {
      const plantName =
        modalMode === "create"
          ? buildPlantName(currentPlant.namePrefix, currentPlant.nameNumber)
          : currentPlant.name;

      if (!plantName) {
        return;
      }

      if (
        modalMode === "create" &&
        (!currentPlant.clientId ||
          !currentPlant.namePrefix ||
          !currentPlant.nameNumber)
      ) {
        return;
      }

      // Prepare payload according to CreatePlantDto
      const payload = {
        name: plantName,
        clientId: currentPlant.clientId || null,
        description: currentPlant.description?.trim() || null,
        sharepointLink: currentPlant.sharepointLink || undefined,
        people:
          currentPlant.people && currentPlant.people.length > 0
            ? currentPlant.people
            : undefined,
      };

      if (modalMode === "create") {
        await secondAxios.post("/plant", payload);
        // Refresh plants list to get filtered results
        await fetchPlants();
      } else {
        await secondAxios.put(`/plant/${currentPlant.id}`, payload);
        // Refresh plants list to get updated data
        await fetchPlants();
      }
      handleCloseModal();
    } catch (err) {
      console.error(
        `Error ${modalMode === "create" ? "creating" : "updating"} plant:`,
        err,
      );
      setState((prev) => ({ ...prev, isError: true }));
    }
  };

  // Delete plant
  const handleDeletePlant = async (plantId) => {
    if (!window.confirm("Are you sure you want to delete this plant?")) {
      return;
    }

    try {
      await secondAxios.delete(`/plant/${plantId}`);
      // Refresh plants list to get filtered results
      await fetchPlants();
    } catch (err) {
      console.error("Error deleting plant:", err);
      setState((prev) => ({ ...prev, isError: true }));
    }
  };

  // Columns definition
  const columns = useMemo(
    () =>
      filterColumnsByLicence(
        [
          // { accessorKey: "id", header: "ID", size: 50 },
          {
            accessorKey: "name",
            header: "Number",
            size: 100,
            minSize: 100,
            grow: 1,
            enableColumnFilter: true,
          },
          {
            accessorKey: "description",
            header: "Description",
            size: 200,
            minSize: 150,
            grow: 1,
            enableColumnFilter: true,
          },
          // {
          //   accessorKey: "quickNav",
          //   header: "Quick Nav",
          //   size: 50,
          //   enableColumnFilter: false,
          //   enableColumnActions: false,
          //   Cell: ({ cell }) => (
          //     <PlantModuleNav
          //       plantId={cell.row.original.id}
          //       compact
          //       sx={{ display: "inline-flex" }}
          //     />
          //   ),
          // },
          {
            id: "client",
            accessorFn: (row) => row.client?.name || row.client?.prefix || "-",
            header: "Client",
            filterVariant: "select",
            size: 10,
            enableColumnFilter: true,
          },
          {
            ...plantIconColumnDefaults,
            accessorKey: "tasks",
            header: "Action Items",
            Header: () => (
              <Box component="span" sx={plantTableHeaderLabelSx}>
                Action
                <br />
                Items
              </Box>
            ),
            enableEditing: false,
            enableColumnActions: false,
            enableColumnFilter: false,
            Edit: () => null,
            Cell: ({ cell }) => {
              return (
                <IconButton
                  size="small"
                  display="flex"
                  alignItems="center"
                  gap={1}
                  noNavigate={true}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/plant/${cell.row.original.id}/task`);
                  }}
                >
                  <AssignmentIcon color="primary" />
                </IconButton>
              );
            },
          },
          // {
          //   accessorKey: "ga",
          //   header: "GA",
          //   Cell: ({ cell }) => {
          //     return (
          //       // /projects/2b75e738-159c-4907-b605-1489f5c653c6/equipments/main_tank#main-nav=pv&mcs-tab=Spec&pv-modeling=Estimation
          //       <Box
          //         display="flex"
          //         alignItems="center"
          //         gap={1}
          //         noNavigate={true}
          //         onClick={(e) => {
          //           e.stopPropagation();
          //           // console.log(
          //           //   "mcs clicked",
          //           //   // `/projects/${cell.row.original.id}/equipments/main_tank#main-nav=${cell.row.original.type}&mcs-tab=Doc.%Estimation`
          //           //   cell.row.original
          //           // );
          //           navigate(
          //             `/projects/${
          //               cell.row.original.id
          //             }/equipments/main_tank#main-nav=${"ga"}&mcs-tab=Spec`
          //           );
          //         }}
          //       >
          //         <CategoryIcon />
          //       </Box>
          //     );
          //   },
          // },
          ...(isEstimation
            ? [
                {
                  ...plantIconColumnDefaults,
                  accessorKey: "estimation",
                  header: "Estimation",
                  enableColumnFilter: false,
                  Cell: ({ cell }) => {
                    return (
                      // /projects/2b75e738-159c-4907-b605-1489f5c653c6/equipments/main_tank#main-nav=pv&mcs-tab=Spec&pv-modeling=Estimation
                      <IconButton
                        size="small"
                        display="flex"
                        alignItems="center"
                        gap={1}
                        noNavigate={true}
                        onClick={(e) => {
                          e.stopPropagation();
                          // console.log(
                          //   "mcs clicked",
                          //   // `/projects/${cell.row.original.id}/equipments/main_tank#main-nav=${cell.row.original.type}&mcs-tab=Doc.%Estimation`
                          //   cell.row.original
                          // );
                          navigate(`/plant/${cell.row.original.id}/estimation`);
                        }}
                      >
                        <FunctionsIcon color="primary" />
                      </IconButton>
                    );
                  },
                },
              ]
            : []),
          ...(!isEstimation
            ? [
                {
                  ...plantIconColumnDefaults,
                  accessorKey: "dcs",
                  header: "EMS",
                  enableColumnFilter: false,
                  Cell: ({ cell }) => {
                    return (
                      // /projects/2b75e738-159c-4907-b605-1489f5c653c6/equipments/main_tank#main-nav=pv&mcs-tab=Spec&pv-modeling=Estimation
                      <IconButton
                        size="small"
                        display="flex"
                        alignItems="center"
                        gap={1}
                        noNavigate={true}
                        onClick={(e) => {
                          e.stopPropagation();
                          // console.log(
                          //   "mcs clicked",
                          //   // `/projects/${cell.row.original.id}/equipments/main_tank#main-nav=${cell.row.original.type}&mcs-tab=Doc.%Estimation`
                          //   cell.row.original
                          // );
                          navigate(`/plant/${cell.row.original.id}/dcs`);
                        }}
                      >
                        <DocumentScannerIcon color="primary" />
                      </IconButton>
                    );
                  },
                },
                {
                  ...plantIconColumnDefaults,
                  accessorKey: "mcs",
                  header: "PMS",
                  enableEditing: false,
                  Edit: () => null,
                  enableColumnActions: false,
                  enableColumnFilter: false,
                  Cell: ({ cell, row }) => {
                    // console.log("row", row.original);
                    // const users = row.original?.users || [];

                    return (
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          // console.log("mcs clicked");
                          navigate(`/plant/${cell.row.original.id}/mcs`);
                          // navigate(
                          //   `/projects/${row.original.id}/equipments/main_tank#main-nav=MCS&mcs-tab=Drawing%20BOM`
                          // );
                        }}
                        // disabled={true}
                      >
                        <ManageAccountsIcon color="primary" />
                      </IconButton>
                    );
                  },
                },

                {
                  ...plantIconColumnDefaults,
                  accessorKey: "pcs",
                  header: "CMS",
                  enableEditing: false,
                  Edit: () => null,
                  enableColumnActions: false,
                  enableColumnFilter: false,
                  Cell: ({ cell }) => {
                    return (
                      <IconButton
                        size="small"
                        display="flex"
                        alignItems="center"
                        gap={1}
                        noNavigate={true}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/plant/${cell.row.original.id}/pcs`);
                        }}
                      >
                        <ConstructionIcon color="primary" />
                      </IconButton>
                    );
                  },
                },
                {
                  ...plantIconColumnDefaults,
                  accessorKey: "validation",
                  header: "Validation",
                  enableEditing: false,
                  Edit: () => null,
                  enableColumnActions: false,
                  enableColumnFilter: false,
                  Cell: ({ cell, row }) => {
                    // console.log("row", row.original);
                    // const users = row.original?.users || [];

                    return (
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/plant/${cell.row.original.id}/validation`);
                        }}
                      >
                        <ChangeHistoryIcon color="primary" />
                      </IconButton>
                    );
                  },
                },
                {
                  ...plantIconColumnDefaults,
                  accessorKey: "cms",
                  header: "COS",
                  enableEditing: false,
                  Edit: () => null,
                  enableColumnActions: false,
                  enableColumnFilter: false,
                  Cell: ({ cell }) => {
                    return (
                      <IconButton
                        size="small"
                        display="flex"
                        alignItems="center"
                        gap={1}
                        noNavigate={true}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/plant/${cell.row.original.id}/cms`);
                        }}
                      >
                        <ChangeCircleIcon color="primary" />
                      </IconButton>
                    );
                  },
                },
                {
                  ...plantIconColumnDefaults,
                  accessorKey: "pms",
                  header: "PCS",
                  enableEditing: false,
                  enableColumnActions: false,
                  enableColumnFilter: false,
                  Edit: () => null,
                  Cell: ({ row, cell }) => {
                    return (
                      // <a
                      //   href={`/projects/${row.original.id}/equipments/main_tank#main-nav=PMS&pms-tab=4D`}
                      // >
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          console.log("pms clicked");
                          e.stopPropagation();
                          navigate(`/plant/${cell.row.original.id}/pms`);
                        }}
                      >
                        <CalendarTodayIcon color="primary" fontSize="small" />
                      </IconButton>
                      // </a>
                    );
                  },
                },

                {
                  ...plantIconColumnDefaults,
                  accessorKey: "mis",
                  header: "MIS",
                  enableEditing: false,
                  Edit: () => null,
                  enableColumnActions: false,
                  enableColumnFilter: false,
                  Cell: ({ cell, row }) => {
                    // console.log("row", row.original);
                    // const users = row.original?.users || [];

                    return (
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          // console.log("mcs clicked");
                          navigate(`/plant/${cell.row.original.id}/mis`);
                          // navigate(
                          //   `/projects/${row.original.id}/equipments/main_tank#main-nav=MCS&mcs-tab=Drawing%20BOM`
                          // );
                        }}
                        // disabled={true}
                      >
                        <DescriptionIcon color="primary" />
                      </IconButton>
                    );
                  },
                },
              ]
            : []),
          {
            accessorKey: "people",
            header: "People",
            size: 600,
            minSize: 140,
            grow: false,
            enableColumnFilter: false,
            Cell: ({ cell }) => {
              const people = cell.getValue();
              if (!people || !Array.isArray(people) || people.length === 0) {
                return <span>-</span>;
              }
              return (
                <span>
                  {people
                    .map((user) =>
                      typeof user === "object" ? user.name || user.email : user,
                    )
                    .join(", ")}
                </span>
              );
            },
          },
          {
            accessorKey: "projects",
            header: "No. sub projects",
            size: 100,
            maxSize: 72,
            grow: false,
            enableSorting: false,
            enableColumnActions: false,
            muiTableHeadCellProps: {
              sx: {
                ...mrtHeadCellKeepLabelsVisibleSx,
                whiteSpace: "normal",
                textAlign: "center",
                verticalAlign: "middle",
                lineHeight: 1.2,
                py: 0.75,
                ...plantCellBorderSx,
              },
            },
            muiTableBodyCellProps: {
              sx: {
                textAlign: "center",
                verticalAlign: "middle",
                px: 0.5,
                ...plantCellBorderSx,
              },
            },
            Header: () => (
              <Box component="span" sx={plantTableHeaderLabelSx}>
                No. of
                <br />
                Sub Projects
              </Box>
            ),
            enableColumnFilter: false,
            Cell: ({ cell, row }) => {
              const plant = row.original;
              const projects = plant.projects || plant.project;

              if (!projects) {
                return <span>0</span>;
              }

              // Handle different data structures
              const projectsArray = Array.isArray(projects)
                ? projects
                : projects
                  ? [projects]
                  : [];

              // Filter by client name if needed
              const clientName = process.env.REACT_APP_CLIENT_NAME;
              const filteredProjects = projectsArray.filter(
                (p) => p && p.clientName === clientName,
              );

              return <span>{filteredProjects.length}</span>;
            },
          },
          {
            accessorKey: "aimsStatus",
            header: "AIMS",
            size: 108,
            minSize: 108,
            maxSize: 108,
            grow: false,
            enableSorting: false,
            enableColumnActions: false,
            muiTableHeadCellProps: {
              sx: {
                ...mrtHeadCellKeepLabelsVisibleSx,
                textAlign: "center",
                verticalAlign: "middle",
                px: 0.5,
                ...plantCellBorderSx,
              },
            },
            muiTableBodyCellProps: {
              sx: {
                textAlign: "center",
                verticalAlign: "middle",
                px: 0.5,
                ...plantCellBorderSx,
              },
            },
            enableEditing: false,
            Edit: () => null,
            enableColumnFilter: false,
            Cell: ({ row }) => {
              const rowId = row.original.id;
              const isAimsSent = Boolean(row.original.aimsSent);
              const isSending = aimsSendingId === rowId;
              return (
                <Chip
                  size="small"
                  sx={{
                    maxWidth: "100%",
                    height: "auto",
                    "& .MuiChip-label": {
                      whiteSpace: "normal",
                      lineHeight: 1.2,
                      py: 0.25,
                    },
                  }}
                  label={
                    isSending
                      ? "Sending..."
                      : isAimsSent
                        ? "Sent To AIMS"
                        : "Send To AIMS"
                  }
                  icon={isAimsSent ? <CheckCircleIcon /> : <AddIcon />}
                  variant={isAimsSent ? "filled" : "outlined"}
                  color={isAimsSent ? "success" : "primary"}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSendToAIMS(rowId);
                  }}
                  disabled={isAimsSent || isSending}
                  onDelete={undefined}
                />
              );
            },
          },
        ],
        hasModule,
        allowedModules,
        user,
      ),
    [
      aimsSendingId,
      plantIconColumnDefaults,
      plantCellBorderSx,
      plantIconColumnSeparatorSx,
      isEstimation,
      hasModule,
      allowedModules,
      user,
    ],
  );

  // Top toolbar actions
  const renderTopToolbarCustomActions = () => (
    <Box sx={{ display: "flex", gap: 1, p: 1 }}>
      <Button
        color="primary"
        variant="contained"
        startIcon={<AddIcon />}
        onClick={handleOpenCreateModal}
      >
        Add Plant
      </Button>
      <Button
        color="primary"
        variant="outlined"
        onClick={() => setClientsModalOpen(true)}
      >
        Manage Clients
      </Button>
    </Box>
  );

  return (
    <>
      <MaterialReactTable
        data={plants}
        columns={columns}
        layoutMode="grid"
        defaultColumn={{
          grow: 1,
          minSize: 96,
          enableSorting: false,
          enableColumnActions: false,
        }}
        displayColumnDefOptions={{
          "mrt-row-actions": {
            header: "Edit",
            size: PLANT_ICON_COLUMN_SIZE,
            minSize: PLANT_ICON_COLUMN_SIZE,
            maxSize: PLANT_ICON_COLUMN_SIZE,
            grow: false,
            enableSorting: false,
            enableColumnActions: false,
            muiTableHeadCellProps: {
              sx: {
                ...mrtHeadCellKeepLabelsVisibleSx,
                textAlign: "center",
                px: 0.5,
                ...plantIconColumnSeparatorSx,
              },
            },
            muiTableBodyCellProps: {
              sx: {
                textAlign: "center",
                px: 0.25,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                ...plantIconColumnSeparatorSx,
                "& .Mui-TableBodyCell-Content-Wrapper": {
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "100%",
                },
              },
            },
          },
        }}
        enableRowActions
        enableSorting={false}
        enableColumnActions={false}
        enableFacetedValues
        enableTableHead
        initialState={{ showColumnFilters: true }}
        positionActionsColumn="last"
        {...mrtSurfaceProps}
        muiTableHeadRowProps={{
          sx: {
            alignItems: "stretch",
            backgroundColor: "background.default",
            "& .MuiTableCell-root": {
              verticalAlign: "middle",
              backgroundColor: "background.default",
            },
          },
        }}
        muiTableHeadCellProps={{
          sx: {
            ...mrtHeadCellKeepLabelsVisibleSx,
            backgroundColor: "background.default",
            ...plantCellBorderSx,
          },
        }}
        muiTableBodyCellProps={{
          sx: {
            fontSize: "1rem",
            backgroundColor: "background.paper",
            ...plantCellBorderSx,
          },
        }}
        muiTablePaperProps={{
          elevation: 0,
          sx: {
            backgroundColor: "background.paper",
            border: 1,
            borderColor: "divider",
          },
        }}
        muiTopToolbarProps={{
          sx: {
            backgroundColor: "background.paper",
            borderBottom: 1,
            borderColor: "divider",
          },
        }}
        muiBottomToolbarProps={{
          sx: {
            backgroundColor: "background.paper",
            borderTop: 1,
            borderColor: "divider",
          },
        }}
        muiTableContainerProps={{
          sx: {
            "& .MuiTableCell-root": {
              borderColor: "divider",
            },
          },
        }}
        renderTopToolbarCustomActions={renderTopToolbarCustomActions}
        muiTableBodyRowProps={({ row }) => ({
          onClick: (event) => {
            const target = event.target;
            if (
              !target.closest(".MuiButtonBase-root") &&
              !target.closest("a")
            ) {
              // console.log("id", paths.plant, row.original);
              // navigate(paths.plant + "/" + row.original.id);
            }
          },
          sx: {
            cursor: "pointer",
            backgroundColor: "background.paper",
            "&:nth-of-type(even)": {
              backgroundColor: "action.hover",
            },
          },
        })}
        renderRowActions={({ row }) => (
          <Box sx={{ display: "flex", gap: 0.5 }}>
            <IconButton
              color="primary"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleOpenEditModal(row.original);
              }}
            >
              <EditIcon />
            </IconButton>
          </Box>
        )}
        muiToolbarAlertBannerProps={
          state.isError
            ? { color: "error", children: "Error loading plant data" }
            : undefined
        }
        state={{
          isLoading: state.isLoading,
          showAlertBanner: state.isError,
        }}
      />

      {/* Create/Edit Modal */}
      <Dialog
        open={modalOpen}
        onClose={handleCloseModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {modalMode === "create" ? "Create New Plant" : "Edit Plant"}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
            <Autocomplete
              options={allClients}
              getOptionLabel={(option) =>
                option?.name
                  ? `${option.name}${option.prefix ? ` (${option.prefix})` : ""}`
                  : ""
              }
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
              value={
                allClients.find((c) => c.id === currentPlant.clientId) || null
              }
              onChange={(_event, newValue) => {
                handleClientChange(newValue);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Client"
                  required={modalMode === "create"}
                  helperText="Prefix is taken from the selected client"
                />
              )}
            />
            {modalMode === "create" ? (
              <Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    flexWrap: "wrap",
                  }}
                >
                  <TextField
                    label="Prefix"
                    value={currentPlant.namePrefix}
                    disabled
                    required
                    sx={{ flex: "1 1 100px", minWidth: 100 }}
                    placeholder="Select client"
                    helperText={!currentPlant.clientId ? "From client" : " "}
                  />
                  <Box component="span" sx={{ fontWeight: 600 }}>
                    -
                  </Box>
                  <TextField
                    label="Number"
                    value={currentPlant.nameNumber}
                    onChange={(e) => handleNameNumberChange(e.target.value)}
                    required
                    sx={{ flex: "1 1 140px", minWidth: 140 }}
                    placeholder="p001"
                    helperText="e.g. p001 or p-001"
                    inputProps={{ maxLength: 20 }}
                  />
                </Box>
                <Box
                  sx={{
                    mt: 1,
                    color: "text.secondary",
                    fontSize: "0.875rem",
                  }}
                >
                  Name preview:{" "}
                  {buildPlantName(
                    currentPlant.namePrefix,
                    currentPlant.nameNumber,
                  ) || "—"}
                </Box>
              </Box>
            ) : (
              <TextField
                label="Plant Name"
                value={currentPlant.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
                fullWidth
                required
              />
            )}
            <TextField
              label="Description"
              value={currentPlant.description || ""}
              onChange={(e) =>
                handleInputChange("description", e.target.value)
              }
              fullWidth
              multiline
              minRows={2}
              helperText="Optional plant description"
            />
            <TextField
              label="SharePoint Link"
              value={currentPlant.sharepointLink}
              onChange={(e) =>
                handleInputChange("sharepointLink", e.target.value)
              }
              fullWidth
              type="url"
              helperText="Optional SharePoint URL"
            />
            <Autocomplete
              multiple
              options={allUsers}
              getOptionLabel={(option) =>
                typeof option === "string"
                  ? allUsers.find((u) => u.id === option)?.name ||
                    allUsers.find((u) => u.id === option)?.email ||
                    option
                  : option.name || option.email || ""
              }
              isOptionEqualToValue={(option, value) => {
                const optionId =
                  typeof option === "string" ? option : option.id;
                const valueId = typeof value === "string" ? value : value.id;
                return optionId === valueId;
              }}
              value={
                currentPlant.people && allUsers.length > 0
                  ? currentPlant.people
                      .map((id) => allUsers.find((u) => u.id === id))
                      .filter((user) => user !== undefined)
                  : []
              }
              onChange={(event, newValue) => {
                handlePeopleChange(newValue);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="People"
                  helperText="Select users to associate with this plant"
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                  const label =
                    typeof option === "string"
                      ? allUsers.find((u) => u.id === option)?.name ||
                        allUsers.find((u) => u.id === option)?.email ||
                        option
                      : option.name || option.email || "";
                  return (
                    <Chip
                      variant="outlined"
                      label={label}
                      {...getTagProps({ index })}
                      key={typeof option === "string" ? option : option.id}
                    />
                  );
                })
              }
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseModal}
            color="secondary"
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSavePlant}
            variant="contained"
            color="primary"
            disabled={
              modalMode === "create"
                ? !currentPlant.clientId ||
                  !currentPlant.namePrefix ||
                  !currentPlant.nameNumber
                : !currentPlant.name
            }
          >
            {modalMode === "create" ? "Create" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <ClientsCrudModal
        open={clientsModalOpen}
        onClose={() => setClientsModalOpen(false)}
        onClientsChange={fetchClients}
      />
    </>
  );
}
